import React, { lazy, Suspense, useState } from "react";

import ExamGenerator from "./exams/ExamGenerator";
import TeacherExamList from "./exams/TeacherExamList";
import TeacherDashboard from "./TeacherDashboard";
import TeacherNavigation from "./TeacherNavigation";
import TeacherWallet from "./wallet/TeacherWallet";
import TeacherAdminMessages from "./messages/TeacherAdminMessages";
import TeacherEstablishments from "./establishments/TeacherEstablishments";
import useTeacherAccess from "./hooks/useTeacherAccess";
import { SUPERVISION_AREAS, useSupervisionHeartbeat } from "../services/supervisionHeartbeat";
import { BandeauHorsConnexion, MesContenusHorsConnexion } from "../components/HorsConnexion";

const TeacherTD = lazy(() => import("./td/TeacherTD"));
const TeacherSupportProfile = lazy(() => import("./TeacherSupportProfile"));
const TeacherNotifications = lazy(() => import("./notifications/TeacherNotifications"));
const TeacherAccount = lazy(() => import("./account/TeacherAccount"));

export default function TeacherApp({ onLogout }) {
  const {
    dashboard,
    loading,
    error,
    reload,
    refreshDashboard,
    updateWalletBalance,
    updateNotificationUnread,
  } = useTeacherAccess();
  const [view, setView] = useState("dashboard");
  const [resumeExamId, setResumeExamId] = useState(null);
  const [walletRefreshKey, setWalletRefreshKey] = useState(0);

  // Sans ce battement, la présence de l'enseignant/générateur ne se met à
  // jour qu'une fois (à la connexion) : elle expire au bout de quelques
  // minutes côté supervision, même s'il manipule toujours son logiciel
  // (bug signalé sur Windows — "Centre temps réel" le fait disparaître).
  useSupervisionHeartbeat(dashboard, SUPERVISION_AREAS.GENERATOR);

  const navigate = (nextView) => {
    setResumeExamId(null);
    setView(nextView);
  };

  const openExistingExam = (examId) => {
    setResumeExamId(examId);
    setView("exam-generator");
  };

  return (
    <div className="ql-teacher-app">
      {view !== "dashboard" && <TeacherNavigation activeView={view} onNavigate={navigate} onLogout={onLogout} />}
      {view !== "hors-connexion" && (
        <div className="ql-teacher-hc-bandeau"><BandeauHorsConnexion generateur onVoir={() => navigate("hors-connexion")} /></div>
      )}
      {loading && (
        <main className="ql-teacher-state" role="status">
          <span className="spinner-border text-primary" aria-hidden="true"></span>
          <p>Chargement de votre espace enseignant…</p>
        </main>
      )}
      {!loading && error && (
        <main className="ql-teacher-state" role="alert">
          <i className="bi bi-wifi-off"></i>
          <h1>Connexion impossible</h1>
          <p>{error}</p>
          <button type="button" className="btn btn-primary" onClick={reload}>Réessayer</button>
        </main>
      )}
      {!loading && dashboard && view === "dashboard" && (
        <TeacherDashboard data={dashboard} onLogout={onLogout} onStartExam={() => navigate("exam-generator")} onNavigate={navigate} />
      )}
      {!loading && dashboard && ["exams", "drafts", "history"].includes(view) && (
        <TeacherExamList
          key={view}
          mode={view}
          onBack={() => navigate("dashboard")}
          onOpenExam={openExistingExam}
        />
      )}
      {!loading && dashboard && view === "hors-connexion" && (
        <main className="ql-teacher-hc-page">
          <MesContenusHorsConnexion generateur onBack={() => navigate("dashboard")} />
        </main>
      )}
      {!loading && dashboard && view === "wallet" && (
        <TeacherWallet
          wallet={dashboard.wallet}
          refreshKey={walletRefreshKey}
          onRefreshBalance={refreshDashboard}
          onBack={() => navigate("dashboard")}
        />
      )}
      {!loading && dashboard && view === "messages" && (
        <TeacherAdminMessages onBack={() => navigate("dashboard")} />
      )}
      {!loading && dashboard && view === "establishments" && (
        <TeacherEstablishments onBack={() => navigate("dashboard")} onChanged={refreshDashboard} />
      )}
      {!loading && dashboard && view === "td" && (
        <Suspense fallback={<main className="ql-teacher-state" role="status">Chargement des Fiches TD…</main>}>
          <TeacherTD wallet={dashboard.wallet} onWalletRefresh={refreshDashboard} onBack={() => navigate("dashboard")} />
        </Suspense>
      )}
      {!loading && dashboard && view === "soutien" && (
        <Suspense fallback={<main className="ql-teacher-state" role="status">Chargement du profil soutien scolaire…</main>}>
          <TeacherSupportProfile onBack={() => navigate("dashboard")} />
        </Suspense>
      )}
      {!loading && dashboard && view === "notifications" && (
        <Suspense fallback={<main className="ql-teacher-state" role="status">Chargement des notifications…</main>}>
          <TeacherNotifications onUnreadChange={updateNotificationUnread} onBack={() => navigate("dashboard")} />
        </Suspense>
      )}
      {!loading && dashboard && view === "account" && (
        <Suspense fallback={<main className="ql-teacher-state" role="status">Chargement du compte…</main>}>
          <TeacherAccount onProfileChanged={refreshDashboard} onBack={() => navigate("dashboard")} />
        </Suspense>
      )}
      {!loading && dashboard && view === "exam-generator" && (
        <ExamGenerator
          initialExamId={resumeExamId}
          wallet={dashboard.wallet}
          onWalletUpdate={updateWalletBalance}
          onWalletRefresh={refreshDashboard}
          onWalletPayment={() => setWalletRefreshKey((value) => value + 1)}
          onBack={() => navigate("dashboard")}
          onManageEstablishments={() => navigate("establishments")}
        />
      )}
    </div>
  );
}
